import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useMediaQuery } from 'react-responsive';
import { ChevronRight, Dumbbell, Search, X } from 'lucide-react';

import { GET_EXERCISE_FILTERS } from '../graphql/queries';
import { trackEvent, trackPageView } from '../utils/gt';
import { EXERCISE_REGIONS, MAP_REGIONS } from '../utils/exerciseRegions';
import {
  equipmentCounts,
  equipmentOrder,
  groupByMuscle,
  joinList,
  matchesFilters,
  regionCounts,
  searchWords,
  sortExercises,
} from '../utils/exerciseLibrary';
import { useStickyColumn } from '../utils/useStickyColumn';
import { BodyFigure } from '../components/exercises/BodyFigure';
import { EquipmentMenu } from '../components/exercises/EquipmentMenu';
import { ExerciseDetail } from '../components/exercises/ExerciseDetail';
import '../styles/pages/biblioteca.css';

// Where the desktop columns stop as the page scrolls: the room above the layout
const STICKY_GAP = 24;
const OTHER_REGIONS = EXERCISE_REGIONS.map(({ key }) => key).filter((key) => !MAP_REGIONS.includes(key));

// The art is drawn on white to multiply onto a light tile; a dumbbell stands in when there is none or it fails
const ExerciseArt = ({ src }) => {
  const [hasImage, setHasImage] = useState(!!src);
  return (
    <span className="mt-bib-art">
      {hasImage ? <img src={src} alt="" loading="lazy" onError={() => setHasImage(false)} /> : <Dumbbell className="mt-bib-art__none" aria-hidden="true" />}
    </span>
  );
};

const regionAt = (target) => target?.closest?.('[data-region]')?.getAttribute('data-region') || '';

/**
 * The exercise library as a map of the body (owner's pick, A · Mapa): tap muscles on a front and a back figure to pick
 * body parts (several combine), and the list groups what's left by muscle, with a count per group. Search (each word
 * on its own), equipment and the order sit over the list. Desktop from 1200px: three columns, the map, the list and
 * the open exercise (the first in the list until one is picked), the side ones sticky while the page scrolls. Phones,
 * and narrower desktops: an exercise takes the list's place as its own page. The open exercise is in the URL
 * (?e=<exerciseId>), so back closes it and a link opens it
 */
export const ExerciseHub = () => {
  const { t, i18n: { language } } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery({ query: '(min-width: 960px)' });
  const isWide = useMediaQuery({ query: '(min-width: 1200px)' });
  const isSplit = isDesktop && isWide;
  const isPt = language === 'pt';
  const ui = (key, options) => t(`pages.ExerciseHub.ui.${key}`, options);
  const muscle = (key) => t(`exerciseFilters.muscleGroups.${key}`);
  const equipmentName = (key) => t(`exerciseFilters.equipmentCategories.${key}`);
  const formatNumber = (n) => n.toLocaleString(isPt ? 'pt-BR' : 'en-US');

  const [searchTerm, setSearchTerm] = useState('');
  // Body parts combine: none picked means all of them
  const [regions, setRegions] = useState([]);
  const [equipment, setEquipment] = useState('');
  const [sort, setSort] = useState('popular');
  // The part under the mouse, on the map or its chip: lit on the map, named with its count under it
  const [hovered, setHovered] = useState('');

  const layoutRef = useRef(null);
  const mapColumnRef = useRef(null);
  const toolsRef = useRef(null);
  const detailRef = useRef(null);
  const backRef = useRef(null);
  const openedHere = useRef(false);
  const listScroll = useRef(0);
  const wasPaged = useRef(false);

  const { data, loading: isLoading } = useQuery(GET_EXERCISE_FILTERS);

  const getTitle = (attrs) => (isPt && attrs.titlePt ? attrs.titlePt : attrs.title);

  const allExercises = useMemo(
    () => (data?.exercises?.data || []).filter((ex) => ex.attributes?.videoUrl || ex.attributes?.thumbnailUrl),
    [data]
  );

  const filters = useMemo(() => ({ words: searchWords(searchTerm), regions, equipment }), [searchTerm, regions, equipment]);

  const exercises = useMemo(
    () => sortExercises(allExercises.filter((ex) => matchesFilters(ex.attributes, filters)), sort, getTitle, isPt ? 'pt' : 'en'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allExercises, filters, sort, isPt]
  );
  const groups = useMemo(() => groupByMuscle(exercises), [exercises]);
  const countsByRegion = useMemo(() => regionCounts(allExercises, filters), [allExercises, filters]);
  const countsByEquipment = useMemo(() => equipmentCounts(allExercises, filters), [allExercises, filters]);
  const equipmentKeys = useMemo(() => equipmentOrder(allExercises), [allExercises]);

  const openId = searchParams.get('e');
  const openExercise = allExercises.find((ex) => ex.attributes.exerciseId === openId) || null;
  // Three columns always show one: the one picked while it's in the list, or the list's first
  const firstListed = groups[0]?.exercises[0] || null;
  const shownExercise = isSplit ? (openExercise && exercises.includes(openExercise) ? openExercise : firstListed) : openExercise;
  // Phones and narrow desktops: the open exercise takes the list's place
  const isPaged = !isSplit && !!openExercise;

  useEffect(() => {
    trackPageView('exercise_hub', `${t('appUrl')}/exercises`, '/exercises');
  }, [t]);

  useStickyColumn(mapColumnRef, { enabled: isDesktop, gap: STICKY_GAP });
  useStickyColumn(detailRef, { enabled: isSplit && !!shownExercise, gap: STICKY_GAP, resetKey: shownExercise?.id });

  // Desktop: the muscle headings stick under the list's own sticky search and filters
  useLayoutEffect(() => {
    const tools = toolsRef.current;
    const layout = layoutRef.current;
    if (!isDesktop || !tools || !layout) return undefined;
    const measure = () => layout.style.setProperty('--mt-bib-tools-h', `${Math.round(tools.offsetHeight)}px`);
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(tools);
    return () => observer?.disconnect();
  }, [isDesktop, isLoading]);

  // An exercise opened as a page starts at its top, with the way back focused; closing it goes back to where the list was
  useLayoutEffect(() => {
    if (isPaged) {
      window.scrollTo(0, 0);
      backRef.current?.focus({ preventScroll: true });
    } else if (wasPaged.current) {
      window.scrollTo(0, listScroll.current);
    }
    wasPaged.current = isPaged;
  }, [isPaged, openExercise?.id]);

  const select = (exercise) => {
    trackEvent('exercise_hub_view_exercise', `Exercise Hub - View: ${exercise.attributes.title}`, 'exercise_hub', true);
    // As a page it's a new step in the history, so back closes it; beside the list it only changes the column
    if (!isSplit) listScroll.current = window.scrollY;
    openedHere.current = !isSplit;
    setSearchParams({ e: exercise.attributes.exerciseId }, { replace: isSplit });
  };

  const closeDetail = () => {
    if (openedHere.current) navigate(-1);
    else setSearchParams({}, { replace: true });
    openedHere.current = false;
  };

  const toggleRegion = (key) => {
    const next = regions.includes(key) ? regions.filter((region) => region !== key) : [...regions, key];
    setRegions(next);
    trackEvent('exercise_hub_filter_muscle', `Exercise Hub - Filter Regions: ${next.join(', ') || 'all'}`, 'exercise_hub', true);
  };

  const pickEquipment = (key) => {
    setEquipment(key);
    trackEvent('exercise_hub_filter_equipment', `Exercise Hub - Filter Equipment: ${key || 'all'}`, 'exercise_hub', true);
  };

  const hasFilters = !!(filters.words.length || regions.length || equipment);
  const clearFilters = () => {
    setSearchTerm('');
    setRegions([]);
    setEquipment('');
  };

  // Mouse only: a tap on a phone picks the part, it doesn't hover it
  const hoverFrom = (event, key) => {
    if (event.pointerType === 'mouse') setHovered(key);
  };

  const regionLabel = (key) => ui(`regions.${key}`);
  const hint = hovered ? (
    <>
      <b>{regionLabel(hovered)}</b> · {ui('exerciseCount', { count: countsByRegion[hovered] })}
    </>
  ) : regions.length ? (
    regions.map((key, index) => (
      <Fragment key={key}>
        {index > 0 && ' + '}
        <b>{regionLabel(key)}</b>
      </Fragment>
    ))
  ) : (
    ui(isDesktop ? 'mapHintPointer' : 'mapHintTouch')
  );

  const regionChip = (key) => {
    const count = countsByRegion[key];
    const isOn = regions.includes(key);
    return (
      <button
        key={key}
        type="button"
        className={`mt-bib-chip${count || isOn ? '' : ' is-zero'}`}
        aria-pressed={isOn}
        onClick={() => toggleRegion(key)}
        onPointerEnter={(event) => hoverFrom(event, key)}
        onPointerLeave={() => setHovered('')}
      >
        {regionLabel(key)}
        {!isLoading && <span className="mt-bib-chip__count">{count}</span>}
      </button>
    );
  };

  const searchField = (
    <label className="mt-bib-search">
      <Search aria-hidden="true" />
      <span className="mt-sr-only">{ui('search')}</span>
      <input
        type="search"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder={ui('search')}
        autoComplete="off"
        enterKeyHint="search"
      />
      {searchTerm && (
        <button type="button" className="mt-bib-search__clear" onClick={() => setSearchTerm('')} aria-label={ui('clearSearch')}>
          <X aria-hidden="true" />
        </button>
      )}
    </label>
  );

  const rowMeta = (attrs) => {
    const others = (attrs.otherMuscles || []).filter((key) => key && key !== attrs.muscleGroup).map((key) => muscle(key).toLowerCase());
    return [attrs.equipmentCategory && equipmentName(attrs.equipmentCategory), others.length > 0 && ui('rowAlso', { muscles: joinList(others, ui('and')) })]
      .filter(Boolean)
      .join(' · ');
  };

  return (
    // A main tab (phone bar and desktop rail): no way "back" out of it
    <div className={`mt-root mt-root--app mt-bib${isPaged ? ' is-paged' : ''}${filters.words.length ? ' is-searching' : ''}`}>
      <div className="mt-bib-layout" ref={layoutRef}>
        <section className="mt-bib-mapcol" ref={mapColumnRef} aria-labelledby="mt-bib-title" hidden={isPaged && !isDesktop}>
          <h1 id="mt-bib-title" className="mt-bib-title">
            {ui('title')}
            {!isLoading && <span className="mt-bib-title__count">{formatNumber(allExercises.length)}</span>}
          </h1>
          {!isDesktop && searchField}
          <div
            className="mt-bib-map"
            onClick={(event) => {
              const key = regionAt(event.target);
              if (key) toggleRegion(key);
            }}
            onPointerOver={(event) => hoverFrom(event, regionAt(event.target))}
            onPointerLeave={() => setHovered('')}
          >
            <div className="mt-bib-figs">
              <figure className="mt-bib-fig">
                <BodyFigure side="front" selected={regions} hovered={hovered} />
                <figcaption>{ui('front')}</figcaption>
              </figure>
              <figure className="mt-bib-fig">
                <BodyFigure side="back" selected={regions} hovered={hovered} />
                <figcaption>{ui('back')}</figcaption>
              </figure>
            </div>
            <p className="mt-bib-hint">{hint}</p>
          </div>
          <div className="mt-bib-chips" role="group" aria-label={ui('regionsLabel')}>
            {MAP_REGIONS.map(regionChip)}
            <span className="mt-bib-chips__break" aria-hidden="true" />
            {OTHER_REGIONS.map(regionChip)}
          </div>
        </section>

        <section className="mt-bib-listcol" aria-label={ui('listLabel')} hidden={isPaged}>
          <div className="mt-bib-tools" ref={toolsRef}>
            {isDesktop && searchField}
            <div className="mt-bib-tools__row">
              <EquipmentMenu
                value={equipment}
                label={ui('equipment')}
                anyLabel={ui('equipmentAny')}
                options={equipmentKeys.map((key) => ({ key, label: equipmentName(key), count: countsByEquipment[key] || 0 }))}
                onChange={pickEquipment}
              />
              <div className="mt-bib-sort" role="group" aria-label={ui('sortLabel')}>
                {['popular', 'az'].map((key) => (
                  <button key={key} type="button" aria-pressed={sort === key} onClick={() => setSort(key)}>
                    {ui(key === 'az' ? 'sortAz' : 'sortPopular')}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-bib-count">
              <span aria-live="polite">
                {isLoading ? null : hasFilters ? (
                  <>
                    <b>{formatNumber(exercises.length)}</b> {ui('ofTotal', { total: formatNumber(allExercises.length) })}
                  </>
                ) : (
                  ui('exerciseCount', { count: allExercises.length })
                )}
              </span>
              {hasFilters && (
                <button type="button" className="mt-bib-clear" onClick={clearFilters}>
                  {ui('clear')}
                </button>
              )}
            </div>
          </div>

          {isLoading ? (
            <ul className="mt-bib-rows" aria-busy="true">
              {Array.from({ length: 8 }, (_, index) => (
                <li key={index} className="mt-bib-skeleton" />
              ))}
            </ul>
          ) : groups.length === 0 ? (
            <div className="mt-bib-empty">
              <p className="mt-bib-empty__title">{ui('emptyTitle')}</p>
              <p>{ui('empty')}</p>
            </div>
          ) : (
            groups.map((group) => (
              <section key={group.muscle} className="mt-bib-group" aria-labelledby={`mt-bib-group-${group.muscle}`}>
                <h2 id={`mt-bib-group-${group.muscle}`} className="mt-bib-group__title">
                  {muscle(group.muscle)}
                  <span>{group.exercises.length}</span>
                </h2>
                <ul className="mt-bib-rows">
                  {group.exercises.map((exercise) => {
                    const attrs = exercise.attributes;
                    const meta = rowMeta(attrs);
                    return (
                      <li key={exercise.id}>
                        <button
                          type="button"
                          className="mt-bib-row"
                          aria-current={isSplit && shownExercise?.id === exercise.id ? 'true' : undefined}
                          onClick={() => select(exercise)}
                        >
                          <ExerciseArt src={attrs.thumbnailUrl} />
                          <span className="mt-bib-row__text">
                            <span className="mt-bib-row__name">{getTitle(attrs)}</span>
                            {meta && <span className="mt-bib-row__meta">{meta}</span>}
                          </span>
                          <ChevronRight className="mt-bib-row__chevron" aria-hidden="true" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))
          )}
        </section>

        {shownExercise ? (
          <article className="mt-bib-detail" ref={detailRef} aria-label={getTitle(shownExercise.attributes)}>
            <ExerciseDetail key={shownExercise.id} exercise={shownExercise} isPt={isPt} onBack={isSplit ? null : closeDetail} backRef={backRef} />
          </article>
        ) : (
          isSplit &&
          !isLoading && (
            <div className="mt-bib-detail mt-bib-detail--empty">
              <p className="mt-bib-empty__title">{ui('emptyTitle')}</p>
              <p>{ui('detailEmpty')}</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};
